import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { genderOptions, ethnicityOptions, gradeLevelOptions, letterGradeOptions } from './messages';
import './styles.css';

const messages = defineMessages({
  or: {
    id: 'collegeevents.filters.preview.or',
    defaultMessage: ' or ',
  },
  main: {
    id: 'collegeevents.filters.preview.main',
    defaultMessage: 'Students that are {filters}.',
  },
  location: {
    id: 'collegeevents.filters.preview.location',
    defaultMessage: 'within {highlighted}',
  },
  locationHighlighted: {
    id: 'collegeevents.filters.preview.locationHighlighted',
    defaultMessage: '{value} {unit, select, MILES { {value, plural, one {mile} other {miles} } }' +
      ' KILOMETERS { {value, plural, one {kilometer} other {kilometers} } } } of {postalCode}',
  },
  locationHighlightedMiles: {
    id: 'collegeevents.filters.preview.locationHighlightedMiles',
    defaultMessage: '{value} mile{plural} of {postalCode}',
  },
  locationHighlightedKilometers: {
    id: 'collegeevents.filters.preview.locationHighlightedKilometers',
    defaultMessage: '{value} kilometer{plural} of {postalCode}',
  },
  gradeLevel: {
    id: 'collegeevents.filters.preview.grade-level',
    defaultMessage: 'a {values}',
  },
  letterGrade: {
    id: 'collegeevents.filters.preview.letter-grade',
    defaultMessage: 'with a {values} GPA',
  },
});

const OrList = ({ values, options }) => {
  /* istanbul ignore if */
  if (!values?.length) {
    return null;
  } else if (values.length === 1) {
    return <span styleName="bg"><FormattedMessage {...options[values[0]]} /></span>;
  } else {
    return (
      <span>
        {values.slice(0, -2).map((val) =>
          <span key={val}><span styleName="bg"><FormattedMessage {...options[val]} /></span>, </span>)}
        <span styleName="bg"><FormattedMessage {...options[values[values.length - 2]]} /></span>
        <FormattedMessage {...messages.or} />
        <span styleName="bg"><FormattedMessage {...options[values[values.length - 1]]} /></span>
      </span>
    );
  }
};
OrList.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.object,
};

const CollegeEventsFilterPreview =
({ genders, ethnicities, postalCode, distanceUnit, distanceValue, gradeLevels, letterGrades, emptyMessage }) => {
  const parts = [];
  if (genders?.length) {
    parts.push(<OrList values={genders} options={genderOptions} />);
  }
  if (ethnicities?.length) {
    parts.push(<OrList values={ethnicities} options={ethnicityOptions} />);
  }
  if (postalCode && distanceUnit && distanceValue) {
    parts.push(<FormattedMessage
      {...messages.location}
      values={{
        highlighted: (
          <span styleName="bg"><FormattedMessage
            {...messages[distanceUnit === 'MILES' ? 'locationHighlightedMiles' : 'locationHighlightedKilometers']}
            values={{
              value: distanceValue,
              plural: distanceValue > 1 ? 's' : '',
              postalCode,
            }}
          />
          </span>
        ),
      }}
    />);
  }
  if (gradeLevels?.length) {
    parts.push(<FormattedMessage
      {...messages.gradeLevel}
      values={{ values: <OrList values={gradeLevels} options={gradeLevelOptions} /> }}
    />);
  }
  if (letterGrades?.length) {
    parts.push(<FormattedMessage
      {...messages.letterGrade}
      values={{ values: <OrList values={letterGrades} options={letterGradeOptions} /> }}
    />);
  }
  return parts.length ? (
    <span styleName="preview">
      <FormattedMessage
        {...messages.main}
        values={{
          filters: (
            <span data-cy="college-events-filter-preview">{parts.reduce((acc, part) => {
              acc.push(<span key={acc.length}>{part}</span>);
              acc.push(<span key={acc.length}>, </span>);
              return acc;
            }, []).slice(0, -1)}
            </span>
          ),
        }}
      />
    </span>
  ) : <span styleName="preview">{emptyMessage}</span>;
};

CollegeEventsFilterPreview.propTypes = {
  genders: PropTypes.arrayOf(PropTypes.string),
  ethnicities: PropTypes.arrayOf(PropTypes.string),
  postalCode: PropTypes.string,
  distanceUnit: PropTypes.string,
  distanceValue: PropTypes.number,
  gradeLevels: PropTypes.arrayOf(PropTypes.string),
  letterGrades: PropTypes.arrayOf(PropTypes.string),
  emptyMessage: PropTypes.node,
};

export default CollegeEventsFilterPreview;
